import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebounce } from 'react-use';
import { filterStores } from '~/shared/utils';
import { Store } from '~/lib/data-contract/salesforce';

interface UseFilteredStoresReturn {
    filteredStores: Store[];
    filteredMapStores: Store[];
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    isSearching: boolean;
    resetSearch: () => void;
    resetTrigger: number;
}

export const useFilteredStores = (storeList: Store[]): UseFilteredStoresReturn => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedListQuery, setDebouncedListQuery] = useState('');
    const [debouncedMapQuery, setDebouncedMapQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(0);

    // Fast debounce for list updates (800ms)
    useDebounce(
        () => {
            setDebouncedListQuery(searchQuery);
            setIsSearching(false);
        },
        800,
        [searchQuery],
    );

    // Slower debounce for expensive map operations (1500ms)
    useDebounce(
        () => {
            setDebouncedMapQuery(searchQuery);
        },
        1500,
        [searchQuery],
    );

    // Set searching state when user starts typing
    useEffect(() => {
        if (searchQuery.length > 3) {
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
    }, [searchQuery]);

    // Memoized filtered stores for list (faster updates)
    const filteredStores = useMemo(() => {
        const trimmed = debouncedListQuery.trim();
        if (!trimmed || trimmed.length <= 2) {
            return storeList;
        }
        return filterStores({
            stores: storeList,
            searchQuery: trimmed,
        });
    }, [debouncedListQuery, storeList]);

    // Memoized filtered stores for map (slower updates)
    const filteredMapStores = useMemo(() => {
        const trimmed = debouncedMapQuery.trim();
        if (!trimmed || trimmed.length <= 2) {
            return storeList;
        }
        return filterStores({
            stores: storeList,
            searchQuery: trimmed,
        });
    }, [debouncedMapQuery, storeList]);

    // Reset all search state
    const resetSearch = useCallback(() => {
        setSearchQuery('');
        setDebouncedListQuery('');
        setDebouncedMapQuery('');
        setIsSearching(false);
        setResetTrigger((prev) => prev + 1);
    }, []);

    return {
        filteredStores,
        filteredMapStores,
        searchQuery,
        setSearchQuery,
        isSearching,
        resetSearch,
        resetTrigger,
    };
};
